import { useState } from 'react';
import {
	Button,
	Empty,
	Modal,
	Form,
	Input,
	InputNumber,
	Select,
	Switch,
	Divider,
	Space,
	Popconfirm,
	Alert,
	Tag,
	Tooltip,
	message,
} from 'antd';
import {
	PlusOutlined,
	EditOutlined,
	DeleteOutlined,
	ReloadOutlined,
	QuestionCircleOutlined,
	PauseCircleOutlined,
	PlayCircleOutlined,
} from '@ant-design/icons';
import { useGetMachinesQuery } from '../../redux/api/machines';
import {
	useCreateMachineMutation,
	useUpdateMachineMutation,
	useDeleteMachineMutation,
	useMachinePreflightMutation,
	useGetMachinePresenceQuery,
} from '../../redux/api/scheduling';
import { useGetLoggedInUserQuery } from '../../redux/api/users';
import { Link } from 'react-router-dom';
import { MachineObject } from '../../interfaces/Machines.interfaces';

import CustomResponsiveTable from '../CustomResponsiveTable/index.tsx';

// Machine category — mirrors the Shop Floor App MachineType (required there to create a row).
const MACHINE_TYPE_OPTIONS = [
	{ value: 'cnc_mill', label: 'CNC Mill' },
	{ value: 'swiss_lathe', label: 'Swiss Lathe' },
	{ value: 'cnc_saw', label: 'CNC Saw' },
	{ value: 'manual_saw', label: 'Manual Saw' },
	{ value: 'beadblaster', label: 'Beadblaster' },
	{ value: 'tumbler', label: 'Tumbler' },
];
// Physical site — maps to the Shop Floor App Location.
const SITE_OPTIONS = [
	{ value: 'west_bend', label: 'West Bend' },
	{ value: 'cedarburg', label: 'Cedarburg' },
];
const labelFor = (opts: { value: string; label: string }[], v?: string) =>
	v ? opts.find((o) => o.value === v)?.label || v : '—';

// Preflight system labels + result shape (from scheduling/machines/preflight/).
const SYS_LABEL: Record<string, string> = {
	erp: 'ERP',
	shop_floor_app: 'Shop Floor App',
	machine_data_pool: 'Machine Data Pool',
	fastlane_app: 'Fastlane-app',
};
interface PreflightResult {
	reachability: Record<string, boolean | null>;
	uniqueness: { name_taken_in: string[]; ip_taken_in: string[] };
	blocking: boolean;
}
// Advisory warnings (never blocking): unreachable systems + name/IP collisions.
const collectWarnings = (pre: PreflightResult | null): string[] => {
	if (!pre) return [];
	const w: string[] = [];
	Object.entries(pre.reachability || {}).forEach(([k, v]) => {
		if (v === false) w.push(`${SYS_LABEL[k] || k} is unreachable`);
	});
	(pre.uniqueness?.name_taken_in || []).forEach((s) => w.push(`Name already used in ${SYS_LABEL[s] || s}`));
	(pre.uniqueness?.ip_taken_in || []).forEach((s) => w.push(`IP already used in ${SYS_LABEL[s] || s}`));
	return w;
};

interface MachinePresence {
	erp?: boolean;
	shop_floor_app?: boolean | null;
	machine_data_pool?: boolean | null;
	fastlane_app?: boolean | null;
}
// "Found in" cell tag — green ✓ = found, red ✗ = missing, grey ? = unknown (unreachable / unconfigured).
const presenceTag = (label: string, v: boolean | null | undefined, tip?: string) => {
	const tag = (
		<Tag color={v === true ? 'green' : v === false ? 'red' : 'default'} style={{ marginInlineEnd: 4 }}>
			{label} {v === true ? '✓' : v === false ? '✗' : '?'}
		</Tag>
	);
	return tip ? (
		<Tooltip key={label} title={tip}>
			{tag}
		</Tooltip>
	) : (
		<span key={label}>{tag}</span>
	);
};
const FA_DOWNSTREAM_TIP =
	'fastlane-app is downstream of the MDP — it pull-syncs machines from it. ✗ or ? may just mean it hasn’t synced from the MDP yet; it can never hold a machine the MDP doesn’t.';

const MachinesTable = () => {
	const { data, isLoading, refetch } = useGetMachinesQuery({});
	const [createMachine] = useCreateMachineMutation();
	const [updateMachine] = useUpdateMachineMutation();
	const [deleteMachine] = useDeleteMachineMutation();
	const [runPreflight] = useMachinePreflightMutation();
	const {
		data: presenceData,
		refetch: refetchPresence,
		isFetching: presenceLoading,
	} = useGetMachinePresenceQuery(undefined);
	const presenceMap: Record<string, MachinePresence> = (presenceData as any)?.presence || {};
	// Machine CRUD is Shop-Managers-only (enforced server-side); hide the write
	// controls for everyone else. Reads (list, presence, details) stay open.
	const { data: me } = useGetLoggedInUserQuery();
	const isShopManager = !!me?.is_superuser || (me?.groups ?? []).includes('Shop Managers');
	const [modalOpen, setModalOpen] = useState(false);
	const [editingId, setEditingId] = useState<string | number | null>(null);
	const [preflight, setPreflight] = useState<PreflightResult | null>(null);
	const [checking, setChecking] = useState(false);
	const [form] = Form.useForm();

	const openCreate = () => {
		setEditingId(null);
		setPreflight(null);
		form.resetFields();
		setModalOpen(true);
	};

	const openEdit = (record: MachineObject) => {
		setEditingId(record.id as any);
		setPreflight(null);
		form.setFieldsValue(record as any);
		setModalOpen(true);
	};

	const closeModal = () => {
		setModalOpen(false);
		form.resetFields();
		setEditingId(null);
		setPreflight(null);
	};

	const doCreate = async (values: any) => {
		try {
			await createMachine(values).unwrap();
			message.success('Machine created');
			closeModal();
			refetch();
		} catch (err: any) {
			message.error(err?.data?.detail || err?.data?.name?.[0] || 'Error saving machine');
		}
	};

	const handleSubmit = async (values: any) => {
		// Edit path — no preflight.
		if (editingId) {
			try {
				await updateMachine({ id: editingId, ...values }).unwrap();
				message.success('Machine updated');
				closeModal();
				refetch();
			} catch (err: any) {
				message.error(err?.data?.detail || err?.data?.name?.[0] || 'Error saving machine');
			}
			return;
		}
		// Create path — run the (advisory) preflight first, then confirm on warnings.
		setChecking(true);
		let pre: PreflightResult | null = null;
		try {
			pre = await runPreflight({
				name: values.name,
				ip_address: values.ip_address,
				site: values.site,
			}).unwrap();
			setPreflight(pre);
		} catch {
			// Preflight is best-effort; if it fails, fall through and create.
		}
		setChecking(false);
		const warnings = collectWarnings(pre);
		if (warnings.length) {
			Modal.confirm({
				title: 'Preflight found issues',
				content: (
					<ul style={{ margin: 0, paddingLeft: 18 }}>
						{warnings.map((w, i) => (
							<li key={i}>{w}</li>
						))}
					</ul>
				),
				okText: 'Create anyway',
				okButtonProps: { danger: true },
				cancelText: 'Go back',
				onOk: () => doCreate(values),
			});
		} else {
			doCreate(values);
		}
	};

	const handleDelete = async (id: string | number) => {
		try {
			await deleteMachine(id).unwrap();
			message.success('Machine deleted');
			refetch();
		} catch (err: any) {
			message.error(err?.data?.detail || 'Error deleting machine');
		}
	};

	// Retire = deactivate (is_active=false) — the safe "remove from service": it
	// propagates to the Shop Floor App / MDP, keeps history, and is reversible.
	const handleSetActive = async (record: MachineObject, nextActive: boolean) => {
		try {
			await updateMachine({ id: record.id, is_active: nextActive }).unwrap();
			message.success(nextActive ? 'Machine reactivated' : 'Machine retired (deactivated everywhere)');
			refetch();
		} catch (err: any) {
			message.error(err?.data?.detail || 'Error updating machine');
		}
	};

	const tableColumns = [
		{
			title: 'Name',
			render: (record: MachineObject) => record.name,
		},
		{
			title: 'Type',
			render: (record: MachineObject) => labelFor(MACHINE_TYPE_OPTIONS, (record as any).machine_type),
		},
		{
			title: 'Manufacturer',
			render: (record: MachineObject) => record?.machine_model?.manufacturer?.name ?? '—',
		},
		{
			title: 'Model',
			render: (record: MachineObject) => record?.machine_model?.model ?? '—',
		},
		{
			title: 'IP Address',
			render: (record: MachineObject) => record.ip_address ?? '—',
		},
		{
			title: 'Site',
			render: (record: MachineObject) => labelFor(SITE_OPTIONS, (record as any).site),
		},
		{
			title: (
				<Tooltip title="Where this machine exists — SFA = Shop Floor App, MDP = Machine Data Pool, FA = fastlane-app (downstream of the MDP). ✓ found · ✗ missing · ? unreachable/unconfigured.">
					Found in <QuestionCircleOutlined style={{ color: '#999' }} />
				</Tooltip>
			),
			render: (record: MachineObject) => {
				const p = presenceMap[String(record.id)] || {};
				return (
					<span style={{ whiteSpace: 'nowrap' }}>
						{presenceTag('SFA', p.shop_floor_app)}
						{presenceTag('MDP', p.machine_data_pool)}
						{presenceTag('FA ↓', p.fastlane_app, FA_DOWNSTREAM_TIP)}
					</span>
				);
			},
		},
		{
			title: '',
			render: (record: MachineObject) => {
				const isActive = (record as any).is_active !== false;
				return (
					<Space>
						{isShopManager && (
							<>
								<Button icon={<EditOutlined />} size="small" onClick={() => openEdit(record)} />
								<Popconfirm
									title={
										isActive
											? 'Retire this machine? It is deactivated everywhere (SFA / MDP) but kept for history — reversible.'
											: 'Reactivate this machine?'
									}
									okText={isActive ? 'Retire' : 'Reactivate'}
									onConfirm={() => handleSetActive(record, !isActive)}
								>
									<Button size="small" icon={isActive ? <PauseCircleOutlined /> : <PlayCircleOutlined />}>
										{isActive ? 'Retire' : 'Reactivate'}
									</Button>
								</Popconfirm>
								<Popconfirm
									title={
										isActive
											? 'This machine is ACTIVE. Deleting removes it as the source of truth (SFA / MDP retire it). Retire is usually safer — delete anyway?'
											: 'Delete this retired machine?'
									}
									okText="Delete"
									okButtonProps={{ danger: true }}
									onConfirm={() => handleDelete(record.id as any)}
								>
									<Button icon={<DeleteOutlined />} size="small" danger />
								</Popconfirm>
							</>
						)}
						<Link to={`/machines/${record.id}`}>
							<Button type="primary" size="small">
								Details
							</Button>
						</Link>
					</Space>
				);
			},
		},
	];

	const emptyTable = () => {
		return (
			<Empty
				image={Empty.PRESENTED_IMAGE_SIMPLE}
				description={
					<span style={{ color: '#000' }}>
						There are currently no Machines to be displayed.
					</span>
				}
			/>
		);
	};

	return (
		<div style={{ display: 'flex', flexDirection: 'column' }}>
			<div style={{ padding: 12, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
				<Button icon={<ReloadOutlined />} loading={presenceLoading} onClick={() => refetchPresence()}>
					Refresh status
				</Button>
				{isShopManager && (
					<Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
						Add Machine
					</Button>
				)}
			</div>
			<CustomResponsiveTable
				columns={tableColumns}
				data={data}
				pagination={false}
				loading={isLoading}
				emptyTable={emptyTable}
			/>

			{/* Create / Edit Machine — ERP is the source of truth; the identity +
			    connection fields propagate to the Shop Floor App / Machine Data Pool. */}
			<Modal
				title={editingId ? 'Edit Machine' : 'Add Machine'}
				open={modalOpen}
				onCancel={closeModal}
				onOk={() => form.submit()}
				okText={editingId ? 'Save' : 'Run preflight & create'}
				confirmLoading={checking}
				width={640}
				destroyOnClose
			>
				{!editingId && preflight && (
					<Alert
						type={collectWarnings(preflight).length ? 'warning' : 'success'}
						showIcon
						style={{ marginBottom: 12 }}
						message="Preflight — reachability & uniqueness (advisory)"
						description={
							<div>
								<div style={{ marginBottom: 6 }}>
									{Object.entries(preflight.reachability || {}).map(([k, v]) => (
										<Tag key={k} color={v === true ? 'green' : v === false ? 'red' : 'default'}>
											{SYS_LABEL[k] || k}: {v === true ? 'reachable' : v === false ? 'unreachable' : 'n/a'}
										</Tag>
									))}
								</div>
								{collectWarnings(preflight).length === 0 ? (
									<span>No name or IP conflicts across systems.</span>
								) : (
									<ul style={{ margin: 0, paddingLeft: 18 }}>
										{collectWarnings(preflight).map((w, i) => (
											<li key={i}>{w}</li>
										))}
									</ul>
								)}
							</div>
						}
					/>
				)}
				<Form form={form} layout="vertical" onFinish={handleSubmit}>
					<Form.Item name="name" label="Name" rules={[{ required: true }]}>
						<Input placeholder="e.g. VINCE" />
					</Form.Item>
					<Form.Item name="description" label="Description">
						<Input.TextArea rows={2} />
					</Form.Item>

					<Divider orientation="left" plain style={{ fontSize: 12 }}>
						Identity &amp; connection — propagated to Shop Floor App / Machine Data Pool
					</Divider>
					<Form.Item
						name="machine_type"
						label="Type"
						tooltip="Machine category. Required by the Shop Floor App to create the machine there."
					>
						<Select options={MACHINE_TYPE_OPTIONS} placeholder="Select type…" allowClear />
					</Form.Item>
					<Form.Item name="site" label="Site" tooltip="Maps to the Shop Floor App Location.">
						<Select options={SITE_OPTIONS} placeholder="Select site…" allowClear />
					</Form.Item>
					<Form.Item name="ip_address" label="IP Address" tooltip="Required for Machine Data Pool telemetry.">
						<Input placeholder="e.g. 10.3.3.50" />
					</Form.Item>
					<Form.Item name="machine_cell_id" label="Cell ID">
						<Input placeholder="e.g. a" />
					</Form.Item>
					<Form.Item
						name="fastlane_app_id"
						label="Fastlane App ID"
						tooltip="Cross-id to the on-prem Fastlane-app. ERP-owned; propagates to the Shop Floor App."
					>
						<Input placeholder="e.g. 4" />
					</Form.Item>
					<Space size="middle" wrap>
						<Form.Item name="telnet_port" label="Telnet Port" initialValue={9000}>
							<InputNumber min={0} max={65535} />
						</Form.Item>
						<Form.Item name="mtconnect_port" label="MTConnect Port" initialValue={8082}>
							<InputNumber min={0} max={65535} />
						</Form.Item>
						<Form.Item name="dprnt_port" label="DPRNT Port" initialValue={8080}>
							<InputNumber min={0} max={65535} />
						</Form.Item>
					</Space>
					<Form.Item name="vsc_url" label="VSC URL">
						<Input placeholder="e.g. http://10.3.3.50:9000" />
					</Form.Item>
					<Form.Item name="tool_capacity" label="Tool Capacity" initialValue={50}>
						<InputNumber min={0} max={1000} />
					</Form.Item>
					<Form.Item name="prod_enabled" label="Production Enabled" valuePropName="checked" initialValue={true}>
						<Switch />
					</Form.Item>

					<Divider orientation="left" plain style={{ fontSize: 12 }}>
						Scheduling
					</Divider>
					<Form.Item name="attended_efficiency" label="Attended Efficiency (%)" initialValue={75}>
						<InputNumber min={0} max={100} step={5} />
					</Form.Item>
					<Form.Item name="unattended_efficiency" label="Unattended Efficiency (%)" initialValue={50}>
						<InputNumber min={0} max={100} step={5} />
					</Form.Item>
					<Form.Item name="max_unattended_hours" label="Max Unattended Hours" initialValue={16}>
						<InputNumber min={0} max={168} step={1} />
					</Form.Item>
					<Form.Item name="is_active" label="Active" valuePropName="checked" initialValue={true}>
						<Switch />
					</Form.Item>
				</Form>
			</Modal>
		</div>
	);
};

export default MachinesTable;
